import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { difference, isEqualWith } from 'lodash';
import { Subscription } from 'rxjs';
import { UserUtil } from '../../data/utils/user.utils';
import { GenericDialogDto } from '../../data/models/dto/generic-dialog.dto';
import { UsersService } from '../../data/services/user.service';
import { CustomSnackbarService } from '../../ui/components/custom-snackbar/custom-snackbar-service';
import { GenericDialogComponent } from '../../ui/components/generic-dialog/generic-dialog.component';
import { ImageItem } from '../../ui/components/select-simple-list/model/item-image.interface';
import { SnackBarComponent } from '../../ui/components/snack-bar/snack-bar.component';
import { ChannelFormComponent } from './components/channel-form/channel-form.component';
import { ChannelParamsDto } from './dto/channel-params.dto';
import { Channel } from './models/channel.interface';
import { ChannelsService } from './services/channels.service';
import { UserParamsDto } from 'src/app/data/models/dto/user-params.dto';
import { SnackbarTypeEnum } from 'src/app/data/models/enum/snackbar-type.enum';

@Component({
  selector: 'app-channels',
  templateUrl: './channels.component.html',
  styleUrls: ['./channels.component.scss'],
})
export class ChannelsComponent implements OnInit, OnDestroy {
  private get schools() {
    return this.userUtil.getSchools();
  }

  private dialogConfig: MatDialogConfig = {
    disableClose: true,
    autoFocus: true,
  };

  channels: Channel[] = [];

  // employees
  employees: ImageItem[] = [];

  private subscribeDialog = new Subscription();
  private serviceSubscription = new Subscription();

  constructor(
    private readonly userService: UsersService,
    private readonly channelsService: ChannelsService,
    private readonly dialog: MatDialog,
    private readonly snackbarService: CustomSnackbarService,
    private readonly snackBar: SnackBarComponent,
    private readonly userUtil: UserUtil
  ) {}

  ngOnDestroy(): void {
    this.serviceSubscription.unsubscribe();
  }

  ngOnInit() {
    this.getChannels();
    this.loadEmployees();
  }

  //#region service data
  getChannels() {
    const params = this.buildParams(this.schools[0].id);
    const sub = this.channelsService.getAll(params).subscribe(
      (res) => {
        this.channels = res;
      },
      (error) => {
        console.log(error);
        this.snackBar.openSnackBar('Falha ao carregar os canais', 'ok');
      }
    );
    this.serviceSubscription.add(sub);
  }

  createChannel(
    dialogRef: MatDialogRef<ChannelFormComponent, Channel>,
    channel: Channel
  ) {
    this.channelsService
      .create(channel)
      .toPromise()
      .then(
        (_) => {
          this.snackbarService.open(
            'O canal foi criado',
            SnackbarTypeEnum.SUCCESS,
            'center',
            'bottom'
          );
          this.getChannels();
          dialogRef.close();
        },
        (error) => {
          console.log(error);
          this.snackBar.openSnackBar('Falha ao criar novo canal', 'ok');
          dialogRef.close();
        }
      );
  }

  updateChannel(
    dialogRef: MatDialogRef<ChannelFormComponent, Channel>,
    channel: Channel
  ) {
    this.channelsService
      .update(channel)
      .toPromise()
      .then(
        (_) => {
          this.snackbarService.open(
            'As alterações foram salvas',
            SnackbarTypeEnum.SUCCESS,
            'center',
            'bottom'
          );
          this.getChannels();
          dialogRef.close();
        },
        (error) => {
          console.log(error);
          if(error.status === 409){
            this.snackBar.openSnackBar('Verificar tickets em aberto, antes de editar o canal', 'ok');
          } else {
            this.snackBar.openSnackBar('Falha ao editar canal', 'ok');
          }
          this.getChannels();
          dialogRef.close();
        }
      );
  }

  deleteChannel(
    dialogRef: MatDialogRef<GenericDialogComponent, Channel>,
    channelId: string
  ) {
    this.channelsService
      .delete(channelId)
      .toPromise()
      .then(
        (_) => {
          this.getChannels();
          this.snackbarService.open(
            'O canal foi excluído',
            SnackbarTypeEnum.SUCCESS,
            'center',
            'bottom'
          );
          dialogRef.close();
        },
        (error) => {
          console.log(error);
          if(error.status === 409){
            this.snackBar.openSnackBar('Verificar tickets em aberto, antes de remover o canal', 'ok');
          } else {
            this.snackBar.openSnackBar('Falha ao remover canal', 'ok');
          }
          this.getChannels();
          dialogRef.close();
        }
      );
  }
  //#endregion

  //#region load lists
  private async loadEmployees(): Promise<void> {
    const params: UserParamsDto = {
      idsSchool: this.schools.map((s) => s.id),
    };
    const result = await this.userService.getEmployees(params).toPromise();

    this.employees = result.map(
      (user) =>
        <ImageItem>{
          id: user.id,
          imageUrl: user.photo,
          name: user.firstName + ' ' + user.lastName,
          isChecked: false,
        }
    );
  }
  //#endregion

  //#region form dialog
  openFormDialog(channel?: Channel): void {
    this.dialogConfig.data = {
      channel: channel,
      employees: this.employees,
    };
    const dialogRef = this.dialog.open(ChannelFormComponent, this.dialogConfig);

    this.subscribeDialog =
      dialogRef.componentInstance.onCreateChannel.subscribe(
        (channel: Channel) => {
          this.createChannel(dialogRef, channel);
        }
      );

    this.subscribeDialog =
      dialogRef.componentInstance.onUpdateChannel.subscribe(
        (channel: Channel) => {
          if (!this.isEqual(this.dialogConfig.data.channel, channel)) {
            this.openWarningDialog(
              () => this.updateChannel(dialogRef, channel),
              () => dialogRef.close()
            );
          } else {
            this.updateChannel(dialogRef, channel);
          }
        }
      );

    dialogRef.afterClosed().subscribe((result) => {
      this.subscribeDialog.unsubscribe();
    });
  }

  openDeleteChannelDialog(channel: Channel) {
    this.dialogConfig.data = {
      imageUrl: '../../../../../assets/images/img-delete-channel.svg',
      primaryText: 'Você tem certeza que deseja excluir este canal?',
      secondaryText:
        'Os participantes não terão mais acesso a ele. Além disso, todo o histórico de conversas será perdido.',
      btnCancelText: 'Não, cancelar',
      btnConfirmText: 'Sim, excluir',
    } as GenericDialogDto;

    const dialogRef = this.dialog.open(
      GenericDialogComponent,
      this.dialogConfig
    );
    this.subscribeDialog = dialogRef.componentInstance.onConfirm.subscribe(
      () => {
        this.deleteChannel(dialogRef, channel.id);
      }
    );

    dialogRef.afterClosed().subscribe((result) => {
      this.subscribeDialog.unsubscribe();
    });
  }
  //#endregion

  //#region channel item events
  onUpdateChannel(channel: Channel) {
    this.openFormDialog(channel);
  }

  onDeleteChannel(channel: Channel) {
    this.openDeleteChannelDialog(channel);
  }
  //#endregion

  private isEqual(oldChannel: Channel, channel: Channel) {
    return isEqualWith(
      oldChannel,
      channel,
      (obj: Channel, other: Channel) =>
        difference(obj?.idsClassRooms, other.idsClassRooms).length == 0 &&
        difference(obj?.idsEmployees, other.idsEmployees).length == 0
    );
  }

  private openWarningDialog(onConfirm: () => void, onCancel: () => void) {
    this.dialogConfig.data = {
      imageUrl: '../../../../../assets/images/baloes.svg',
      primaryText:
        'Você tem certeza que deseja remover algum atendente ou alguma turma?',
      secondaryText:
        'O(s) atendente(s) e os responsáveis removidos não terão mais acesso ao canal e perderão o histórico de conversas.',
      btnCancelText: 'Não, cancelar',
      btnConfirmText: 'Sim, remover',
    } as GenericDialogDto;

    const dialogRef = this.dialog.open(GenericDialogComponent, {
      ...this.dialogConfig,
      ...{ width: '390px' },
    });
    this.subscribeDialog = dialogRef.componentInstance.onConfirm.subscribe(
      () => {
        onConfirm();
        dialogRef.close();
      }
    );

    dialogRef
      .afterClosed()
      .toPromise()
      .then((res) => {
        if (res === false) onCancel();
      });

    dialogRef.afterClosed().subscribe((result) => {
      this.subscribeDialog.unsubscribe();
    });
  }

  private buildParams(
    idSchool?: string,
    idsClassRooms?: string[]
  ): ChannelParamsDto {
    return {
      idSchool: idSchool,
      idsClassRooms: idsClassRooms,
    } as ChannelParamsDto;
  }
}
